/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { Separator } from '@ba/ba-ui-carbon-toolkit';
import compareTable32 from '@ba/ba-graphics/dist/icons-js/compare_32';
import relatedVisualization16 from '@ba/ba-graphics/dist/icons-js/related-visualization_16';
import referenceLine16 from '@ba/ba-graphics/dist/icons-js/reference-line_16';

import StringResources from 'nls/StringResources';
import ExploreCompareOptions from './ExploreCompareOptions';
import AddNewCardToolbarItem from 'components/cards/AddNewCardToolbarItem';

import Navigation from 'components/toolbar/navigation/Navigation';
import ToolbarSection from 'components/toolbar/ToolbarSection';
import {
	ToolbarItem,
	CustomToolbarItem
} from 'components/toolbar/items';

@observer
class ExploreContainerToolbar extends Component {

	constructor(props) {
		super(props);
		this.state = {
			toolbarSize: undefined
		};
	}

	render() {

		const {
			exploreVM,
			dashboardApi
		} = this.props;

		const selectedCardIds = exploreVM.selectedCards.items;
		const selectedCards = selectedCardIds.map(id => this._getCard(id));
		const isRelatedVizToolbarItemEnabled = exploreVM.isRelatedVizToolbarItemEnabled();

		const cardActionSpec = exploreVM.toolbar.getCardActions(selectedCards);
		const widgets = exploreVM.selectedWidgets;
		let visTypeLabel = '';
		let visTypeIconId;
		const visTypes = [];

		widgets && widgets.forEach(widget => {
			const visId = widget && widget.getVisId && widget.getVisId();
			const visType = visId && this._getVisType(visId);
			if (visType) {
				visTypeLabel = visType.getLabel();
				// show the specific vis icon from the current vis
				const visIcon = this._getVisIconFromVisId(visId);
				const visTypeIcon = visIcon && dashboardApi.getFeature('Icons').getIcon(visIcon);
				visTypeIconId =  visTypeIcon && visTypeIcon.id;
				visTypes.push(visType);
			}
		}, this);
		const isRecommendedHidden = visTypes.length != 1;
		const widgetActionSpec = (visTypes.length >=1 ) ? exploreVM.toolbar.getWidgetActions() : [];

		const navigationButtons = this.renderNavigationButtons();
		const compareButton = this.renderCompareButton();
		const cardActions = this._renderActions(cardActionSpec);
		const widgetActions = this._renderActions(widgetActionSpec);
		const actionSeparator = <Separator className="explore-vis-toolbar__separator" orientation='vertical'/>;

		const compareModel = this._getCompareModelOnSelectedCard();

		let compareTooltipTitle;
		if (compareModel && compareModel.showStats){
			compareTooltipTitle = StringResources.get('exploreToolbar_hide_compare');
		} else {
			compareTooltipTitle = StringResources.get('exploreToolbar_show_compare');
		}

		let refLineTooltipMessage;
		if (compareModel && compareModel.isCompareLineShown()) {
			refLineTooltipMessage = StringResources.get('exploreToolbar_hide_refLine');
		} else {
			refLineTooltipMessage = StringResources.get('exploreToolbar_show_refLine');
		}

		return (
			<ToolbarSection align='left'>
				<AddNewCardToolbarItem
					onAddCard={this.props.onAddCard}
					exploreVM={exploreVM}
				/>
				{actionSeparator}
				{navigationButtons}
				<ToolbarItem
					iconId={visTypeIconId}
					label={visTypeLabel}
					title={visTypeLabel}
					hidden={isRecommendedHidden}
					selected={exploreVM.showModalVisTypePicker}
					onPrimaryAction={this._handleShowVisTypesClick.bind(this)}
					id="changeVisType"
				/>
				{!isRecommendedHidden && actionSeparator}
				<ToolbarItem
					iconId={relatedVisualization16.id}
					label={StringResources.get('exploreToolbar_related')}
					title={StringResources.get('exploreToolbar_related')}
					hidden={!isRelatedVizToolbarItemEnabled || isRecommendedHidden}
					selected={exploreVM.showModalRelatedVisPicker}
					onPrimaryAction={this._handleShowRelatedVisClick.bind(this)}
					id="relatedVis"
				/>
				{isRelatedVizToolbarItemEnabled && !isRecommendedHidden && actionSeparator}
				{compareButton}
				{widgetActions}
				<ToolbarItem
					iconId={referenceLine16.id}
					hidden={!compareModel}
					title={refLineTooltipMessage}
					enabled={!!compareModel && compareModel.isCompareLineEnabled()}
					selected={!!compareModel && compareModel.isCompareLineShown()}
					onPrimaryAction={() => this.onCompareCrosshairClick()}
				/>
				{!!compareModel && actionSeparator}
				<ToolbarItem
					iconId={compareTable32.id}
					hidden={!compareModel}
					title={compareTooltipTitle}
					enabled={!!compareModel && (compareModel.isCompareTableEnabled())}
					selected={!!compareModel && compareModel.showStats}
					onPrimaryAction={() => this.onCompareStatsClick()}
				/>
				{!!compareModel && actionSeparator}
				{compareModel && <ExploreCompareOptions
					model={compareModel}
					enabled={!!compareModel && (compareModel.isSyncMenuEnabled())}
					hidden={!compareModel}
				/>}
				{compareModel && actionSeparator}
				{cardActions}
			</ToolbarSection>
		);
	}

	_getCompareModelOnSelectedCard() {
		const selectedCardId = this._getSelectedCard();
		return (this._getCompareCard(selectedCardId) || {}).compareModel;
	}

	_getCompareCard(id) {
		if (id) {
			const item = this._getCard(id);
			if (item && item.subType==='compare') {
				return item;
			}
		}
	}

	_getCard(id) {
		return id && this.props.boardModel.layout.findModel(id);
	}

	_getSelectedCard() {
		const exploreVM = this.props.exploreVM;
		return !exploreVM.isStartingPointsDisplayed() ? exploreVM.getDisplayedCard() : undefined;
	}

	onCompareCrosshairClick() {
		const compareModel = this._getCompareModelOnSelectedCard();
		if (compareModel) {
			compareModel.toggleShowCompareLine();
		}
	}

	onCompareStatsClick() {
		const compareModel = this._getCompareModelOnSelectedCard();
		if (compareModel) {
			compareModel.setShowStats(!compareModel.showStats);
		}
	}

	onCompareOptionsClick() {
		this.props.exploreVM.toolbar.toggleCompareOptionsContextMenu();
	}

	onCompareButtonClick = () => {
		if (this.props.exploreVM.isNewCompareCardRecommendationsEnabled()) {
			this._onCompareButtonClick_newBehaviour();
		} else {
			this._onCompareButtonClick_oldBehaviour();
		}
	}

	_onCompareButtonClick_newBehaviour() {
		this.props.exploreVM.setShowCompareSlideout(!this.props.exploreVM.showCompareSlideout);
	}

	_onCompareButtonClick_oldBehaviour() {
		const id = this._getSelectedCard();
		if (id) {
			const card = this._getCard(id);
			const newCompareCardId = this.props.exploreVM.layoutModel.addCompareCardFromExisting(card, card);
			if (newCompareCardId) {
				this.props.exploreVM.setDisplayedToCard(newCompareCardId);
				const card = this.props.exploreVM.layoutModel.findModel(newCompareCardId);
				if (card && card.items.length === 2) {
					const widget = card.items[1];
					// We need to explicilty select the widget and slots tab of the compare card
					// when widget becomes visible because by default compare card will deselect all widgets;
					this.props.exploreVM.setItemInCardToSelectWhenVisible({ widgetId: widget.id, tabId: 'slots' });
					const {
						dashboardApi
					} = this.props;
					if (dashboardApi) {
						dashboardApi.getCanvasWhenReady()
							.then((canvasApi) => canvasApi.getWidgetsWhenRendered(card.items.map((item) => item.id)))
							.then(() => this.props.exploreVM.selectItemInCardWhenVisible());
					}
				}
				// should never come here;
			}
		}
	}

	renderNavigationButtons() {

		const { exploreVM } = this.props;
		const currentCardIndex = exploreVM.displayedCardIndex;
		const numberOfCards = exploreVM.numberOfCards;

		if (currentCardIndex < 0 || numberOfCards <= 0) {
			return;
		}

		return (
			<CustomToolbarItem>
				<Navigation
					index={currentCardIndex}
					count={numberOfCards}
					onNavigatePrevious={this._handleNavigatePrevious.bind(this)}
					onNavigateNext={this._handleNavigateNext.bind(this)}
				/>
				<Separator className="explore-vis-toolbar__separator" orientation='vertical'/>
			</CustomToolbarItem>
		);
	}

	renderCompareButton() {
		if (!this._isMappingComplete() || this.props.exploreVM.isCompare()) {
			return;
		}

		return (
			<CustomToolbarItem>
				<ToolbarItem
					selected={false}
					iconId={compareTable32.id}
					label={StringResources.get('exploreToolbar_compare')}
					title={StringResources.get('exploreToolbar_compare_tooltip')}
					onPrimaryAction={this.onCompareButtonClick}
				/>
				<Separator className="explore-vis-toolbar__separator" orientation='vertical'/>
			</CustomToolbarItem>
		);
	}

	_isMappingComplete() {
		const { exploreVM } = this.props;
		return exploreVM.isSelectedWidgetMappingComplete();
	}

	_renderActions(actions) {
		if (actions) {
			return actions.map((action, key) => {
				const icon = this._renderIcon(action.icon);

				return (
					<CustomToolbarItem key={key}>
						<ToolbarItem
							icon={icon}
							title={action.label}
							className={action.className}
							onPrimaryAction={(event) => this._onPrimaryAction(action, event)}
							selected={action.selected}
						/>
						<Separator className="explore-vis-toolbar__separator" orientation='vertical'/>
					</CustomToolbarItem>
				);
			});
		}
	}

	_renderIcon(id) {
		return (
			<svg style={{ width: '16px' }} className="ba-common-svgIcon is-normal" role="img" focusable={false}>
				<use xmlnsXlink="http://www.w3.org/1999/xlink" xlinkHref={`#${id}`} />
			</svg>
		);
	}

	_getVisType(visId){
		const vis =  this.props.exploreVM.visTypes && this.props.exploreVM.visTypes.filter(function(vis){
			return vis.getId() === visId;
		});
		return vis?vis[0]:null;
	}

	_getVisIconFromVisId(id) {
		const visDefinition = this.props.dashboardApi.getFeature('VisDefinitions').getById(id);
		return visDefinition && visDefinition.getIcon();
	}

	_handleNavigatePrevious() {
		this.props.exploreVM.navigatePreviousCard();
	}

	_handleNavigateNext() {
		this.props.exploreVM.navigateNextCard();
	}

	_handleShowVisTypesClick() {
		this.props.exploreVM.setShowModalVisTypePicker(!this.props.exploreVM.showModalVisTypePicker);
	}

	_handleShowRelatedVisClick() {
		this.props.exploreVM.setShowModalRelatedVisPicker(!this.props.exploreVM.showModalRelatedVisPicker);
	}

	_onPrimaryAction({ action }, event) {
		if (action) {
			action(event);
		}
	}

}

ExploreContainerToolbar.propTypes = {
	exploreVM: PropTypes.object.isRequired,
	boardModel: PropTypes.object.isRequired,
	dashboardApi: PropTypes.object.isRequired,
	onAddCard: PropTypes.func.isRequired
};

export default ExploreContainerToolbar;



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/ExploreContainerToolbar.js